import { expandBySelector, ensureActiveBySelector } from '../primitives/GuidedTour';

/**
 * Screener guided-tour step catalog.
 *
 * All screener-specific tour content and prepare helpers live here — the
 * GuidedTour engine and useTourState hook (components/primitives/) stay
 * module-agnostic. Copy mirrors the preset tooltips in utils/presetMappings.js
 * (expiration = theta-decay tradeoff; strike = probability vs leverage).
 *
 * The form phase auto-populates a DEMO selection as its steps appear (via each
 * form's additive `tourPopulate(part, value)` ref method — only-if-empty, so a
 * user's own selections are never overwritten), and the final form step's
 * primary button becomes "Run scan", clicking the real Run button so the
 * results phase resumes on real data.
 *
 * Anchors (data-tour attributes) referenced below:
 *   Form:    mode-toggle, tickers-section, direction-section, strategy-section,
 *            expiration-section, strike-section, form-actions
 *   Results: view-toggle, view-detail, view-heatmap, heatmap-settings,
 *            results-filters, heatmap-controls, heatmap-table, row-actions,
 *            simulation-settings, column-tabs
 */

// --- Demo selection populated by the tour (only into empty sections) --------
// Rich heatmap contrast: ITM + OTM strikes × medium-term + LEAPS expirations.
const DEMO = {
  basic: {
    tickers: ['AAPL'],
    direction: 'call',
    strategy: ['medium-itm-call', 'medium-otm-call', 'long-itm-call', 'long-otm-call'],
  },
  intermediate: {
    tickers: ['AAPL'],
    direction: 'buy_call',
    expiration: ['medium', 'leaps'],
    strike: ['itm', 'otm'],
  },
};

// --- Screener-specific prepare helpers -------------------------------------

// AccordionFormSection: root .accordion-section, header .section-header,
// expanded content .section-content.expanded. One-way expand (never collapses).
const expandAccordion = (sel) =>
  expandBySelector(sel, '.section-header', '.section-content.expanded');

// View switching: ScreenerResponsive toggle buttons carry .active when selected.
export const ensureDetailView = () =>
  ensureActiveBySelector('[data-tour="view-detail"]', 'active');
export const ensureHeatmapView = () =>
  ensureActiveBySelector('[data-tour="view-heatmap"]', 'active');

// HeatmapSettingsPanel root is .simulation-settings; its clickable header is
// the first child div; expanded content contains a range slider.
const expandHeatmapSettings = () =>
  expandBySelector('[data-tour="heatmap-settings"]', '.simulation-settings > div:first-child', 'input[type="range"]');

// Small settle delay so a just-clicked view toggle can mount its tree before
// the engine polls for the step's anchor.
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// --- Step catalog -----------------------------------------------------------

/**
 * @param {string} mode - 'basic' | 'intermediate'
 * @param {Object} formRef - React ref to the active form (from ScreenerResults),
 *   exposing tourPopulate(part, value). Optional — steps degrade gracefully.
 * @param {Object} handlers - { onSelectMode(modeKey) } — lets the mode-choice
 *   step switch the screener mode; the controller regenerates the steps for
 *   the chosen mode. Optional.
 */
export function getScreenerTourSteps(mode, formRef, handlers = {}) {
  const isBasic = mode === 'basic';
  const demo = DEMO[mode] || DEMO.basic;
  const { onSelectMode } = handlers;

  // Expand the step's accordion section, then fill it with the demo value if
  // the user hasn't picked anything there yet.
  const expandAndPopulate = (sel, part) => () => {
    expandAccordion(sel);
    if (formRef?.current?.tourPopulate && demo[part] != null) {
      formRef.current.tourPopulate(part, demo[part]);
    }
  };

  const formSteps = [
    {
      id: 'welcome',
      target: null,
      placement: 'center',
      title: 'Welcome to the Options Screener',
      body: [
        'The screener finds option contracts matching your strategy and projects how they would perform under the scenarios you care about.',
        'This quick tour builds a real demo scan step by step and runs it, so you can see live results. Skip anytime — you can relaunch it from the Guide button next to the page title.',
      ],
    },
    {
      id: 'modes',
      target: '[data-tour="mode-toggle"]',
      title: 'How do you want to screen?',
      body: 'Pick a mode below — the rest of the tour follows your choice. You can always switch modes later from the toggle up top.',
      // Rendered as buttons inside the tour card; picking one switches the
      // screener mode (ScreenerResults.handleModeChange) and the controller
      // regenerates the remaining steps for that mode. Next = keep current.
      choices: onSelectMode
        ? [
            {
              key: 'basic',
              label: 'Basic',
              description: 'Six one-click strategies — best for 3+ month contracts & LEAPS',
              selected: mode === 'basic',
              onSelect: () => onSelectMode('basic'),
            },
            {
              key: 'intermediate',
              label: 'Intermediate',
              description: 'Mix and match expiration windows × strike positions',
              selected: mode === 'intermediate',
              onSelect: () => onSelectMode('intermediate'),
            },
          ]
        : undefined,
    },
    {
      id: 'tickers',
      target: '[data-tour="tickers-section"]',
      prepare: expandAndPopulate('[data-tour="tickers-section"]', 'tickers'),
      title: 'Pick your stocks',
      body: 'We\'ve added AAPL as a demo — search and add or remove tickers anytime. Every strategy you choose runs against every ticker here.',
    },
    {
      id: 'direction',
      target: '[data-tour="direction-section"]',
      prepare: expandAndPopulate('[data-tour="direction-section"]', 'direction'),
      title: 'Choose a market direction',
      body: isBasic
        ? 'Bullish buys calls — you profit if the stock rises. Bearish buys puts — you profit if it falls. We\'ve selected Bullish for the demo.'
        : 'Bullish buys calls — you profit if the stock rises. Bearish buys puts — you profit if it falls. This mode also offers premium selling: cash-secured puts (neutral/bullish income) and selling calls — note the high-risk warning, naked calls have unlimited loss potential. We\'ve selected Bullish for the demo.',
    },
  ];

  if (isBasic) {
    formSteps.push({
      id: 'strategy',
      target: '[data-tour="strategy-section"]',
      prepare: expandAndPopulate('[data-tour="strategy-section"]', 'strategy'),
      title: 'Strategy = expiration + strike in one card',
      body: [
        'The rows are expiration timeframes: 1–3 months (cheapest, but time decay bites fastest), 6–11 months (time for a thesis to develop), and 12+ month LEAPS (minimal daily decay — closest to owning the stock).',
        'The columns are strike position: ITM −25% strikes cost more but win more often and track the stock closely. OTM +25% strikes are cheap, lottery-style leverage that need a big move. For puts the physical direction flips (ITM puts sit above the current price) but the trade-off is the same.',
        'We\'ve selected all four medium-term and LEAPS cards — ITM and OTM — so the results show the contrast side by side.',
      ],
    });
  } else {
    formSteps.push(
      {
        id: 'expiration',
        target: '[data-tour="expiration-section"]',
        prepare: expandAndPopulate('[data-tour="expiration-section"]', 'expiration'),
        title: 'Expiration: how much time your trade gets',
        body: 'Ultra Short (<1 month) carries heavy theta decay — best for quick momentum or earnings plays. Short and Medium balance premium cost against time value. Long (6–12 months) has minimal daily decay for conviction plays, and LEAPS (12+ months) work as stock replacement. We\'ve selected Medium-Term and LEAPS so you can compare a shorter thesis against a long one.',
      },
      {
        id: 'strike',
        target: '[data-tour="strike-section"]',
        prepare: expandAndPopulate('[data-tour="strike-section"]', 'strike'),
        title: 'Strike: probability vs. leverage',
        body: [
          'Negative % = in the money: more expensive, higher probability of profit, real intrinsic value (Deep ITM −50% is essentially stock replacement). 0% = at the money — roughly a coin-flip with ~50 delta. Positive % = out of the money: cheaper and more leveraged, but the stock has to move to pay off (+100% is a pure lottery ticket).',
          'We\'ve pre-selected one ITM and one OTM strike — every expiration × strike combo becomes its own scenario, so you\'ll see both trade-offs in the results heatmap next.',
        ],
      }
    );
  }

  formSteps.push({
    id: 'actions',
    target: '[data-tour="form-actions"]',
    nextLabel: 'Run scan',
    onNext: () => {
      document.querySelector('[data-tour="form-actions"] button:first-child')?.click();
    },
    title: 'Run it',
    body: [
      'Run executes your selections immediately. + Add to Queue stacks multiple setups to run in one batch. Save as preset keeps this configuration for next time (it appears in the panel on the right).',
      'Click Run scan below — we\'ll run this demo setup and the tour continues automatically in the results.',
    ],
  });

  // --- Concrete heatmap example (one contract, one scenario column) --------
  // Chosen fresh on every activation of the example step; the target selector
  // is a function re-evaluated per engine poll tick, so it also handles the
  // table mounting a beat after the view switch.
  let examplePct = null;
  const EXAMPLE_ROW = '[data-tour="heatmap-table"] tbody tr:first-child';

  const pickScenarioPct = () => {
    const ths = document.querySelectorAll('[data-tour="heatmap-table"] thead [data-tour-scenario]');
    const vals = Array.from(ths)
      .map((th) => parseFloat(th.getAttribute('data-tour-scenario')))
      .filter((v) => !isNaN(v));
    if (vals.length === 0) return null;
    // Prefer the positive move closest to +5% (the default set includes it).
    // Falls back to any column if the user deselected every positive scenario.
    const pool = vals.some((v) => v > 0) ? vals.filter((v) => v > 0) : vals;
    return pool.reduce((best, v) => (Math.abs(v - 5) < Math.abs(best - 5) ? v : best), pool[0]);
  };

  const readCellText = (sel) => {
    const el = document.querySelector(sel);
    if (!el) return null;
    const text = (el.textContent || '').replace('↗', '').trim();
    return text || null;
  };

  const resultsSteps = [
    {
      id: 'view-toggle',
      target: '[data-tour="view-toggle"]',
      prepare: ensureHeatmapView,
      title: 'Three views of the same results',
      body: 'Elasticity Heatmap (the default) shows projected returns across stock-move scenarios. Detail View is the full data table. Stories is a card-based summary. The tour covers the heatmap first, then the Detail View.',
    },
    {
      id: 'heatmap-settings',
      target: '[data-tour="heatmap-settings"]',
      prepare: async () => {
        ensureHeatmapView();
        await wait(150);
        expandHeatmapSettings();
      },
      title: 'Days to Exit and scenario columns',
      body: [
        'The slider sets how long you plan to hold before selling — as fixed days or as a % of each contract\'s life (it starts at 50%). Every projection in the heatmap is recalculated for that exit date.',
        'The scenario chips control which stock-move columns appear (−10%, +5%, +20%, …). Reset restores the defaults.',
      ],
    },
    {
      id: 'filters',
      target: '[data-tour="results-filters"]',
      title: 'Filter the results',
      body: 'Three filters narrow what you see in every view: Expiration date, Ticker, and Strike Position (labels like "ITM (−25%)"). Combine them freely — they only filter what\'s shown, they never re-run the scan.',
    },
    {
      id: 'display-controls',
      target: '[data-tour="heatmap-controls"]',
      title: 'Multiplier vs. Premium Gain — plus your toolkit',
      body: [
        'Premium Gain % shows the option\'s projected % return in each scenario. Underlying Multiplier shows leverage: option gain ÷ stock move, so "5x" means the option gains ~5% for every 1% the stock moves.',
        'On the right: show Favorites only, Share the heatmap as an image, or Export CSV.',
      ],
    },
    {
      id: 'heatmap-example',
      prepare: () => {
        ensureHeatmapView();
        examplePct = null; // re-picked by the target function once the table is in the DOM
      },
      target: () => {
        if (examplePct == null) examplePct = pickScenarioPct();
        if (examplePct == null) return null; // no scenario columns → engine auto-skips
        return `${EXAMPLE_ROW} [data-tour-scenario="${examplePct}"]`;
      },
      extraTargets: () => [
        `[data-tour="heatmap-table"] thead [data-tour-scenario="${examplePct}"]`,
        `${EXAMPLE_ROW} [data-tour-col="days-to-exit"]`,
      ],
      title: 'Read one cell like a sentence',
      body: () => {
        const pct = examplePct;
        const ticker = readCellText(`${EXAMPLE_ROW} .table-cell-sticky-1`) || 'the first contract';
        const type = readCellText(`${EXAMPLE_ROW} .table-cell-sticky-3`) || '';
        const strike = readCellText(`${EXAMPLE_ROW} .table-cell-sticky-4`) || '';
        const days = readCellText(`${EXAMPLE_ROW} [data-tour-col="days-to-exit"]`);
        const cellText = pct != null
          ? readCellText(`${EXAMPLE_ROW} [data-tour-scenario="${pct}"]`)
          : null;

        const contract = [ticker, strike, type].filter(Boolean).join(' ');
        const dir = pct > 0 ? 'rising' : pct < 0 ? 'falling' : 'staying flat';
        const movePhrase = pct === 0 ? 'staying flat' : `${dir} ${Math.abs(pct)}%`;
        const isMultiplier = cellText != null && /x$/i.test(cellText);
        const outcome = !cellText || cellText === '--'
          ? 'shows the projected result of exactly that move'
          : isMultiplier
            ? `is projected to move ${cellText.replace(/x$/i, '×')} the stock's move`
            : `is projected to change ${cellText}`;
        const daysPhrase = days ? `${days} days out — the highlighted Days-to-Exit value` : 'the highlighted Days-to-Exit value';

        return [
          'Each row is one contract; each colored column is a stock-move scenario. Let’s read one cell like a sentence.',
          `Take the first row — the ${contract}. The highlighted column header is the stock move: ${ticker} ${movePhrase}. The highlighted cell is the payoff: if that move happens by your exit date (${daysPhrase}), Black-Scholes says this contract’s premium ${outcome}.`,
          'Every cell reads the same way: stock moves X% by your exit date → premium changes Y%. Hover any cell for the projected premium, underlying price, and breakeven; click a ticker for its chart.',
        ];
      },
    },
    {
      id: 'row-actions',
      target: '[data-tour="row-actions"]',
      title: 'Keep, save, or dismiss',
      body: 'The star pins a contract as a favorite — it floats to the top and enables the Favorites filter. The plus saves it to your Watchlist (you pick which groups it joins). The minus hides a row you\'ve ruled out.',
    },
    {
      id: 'detail-sim',
      target: '[data-tour="simulation-settings"]',
      prepare: async () => {
        ensureDetailView();
        await wait(250);
      },
      title: 'The Detail View: every number behind the heatmap',
      body: 'This is the full table. Its Simulation Settings add an Underlying Gain slider alongside Days to Exit — drag either and every simulated column recomputes instantly in your browser. No new data is fetched, so experiment freely.',
    },
    {
      id: 'column-tabs',
      target: '[data-tour="column-tabs"]',
      title: '30+ columns, organized',
      body: 'Use the tabs to focus the table: Simulation (projected returns), Sell Metrics, Contract Details, Greeks, and Market Data — or All. Hover any column header for a plain-English explanation of that metric. The same filters and row actions from the heatmap work here too, and you can export everything to CSV.',
    },
    {
      id: 'done',
      target: null,
      placement: 'center',
      title: "You're set",
      body: [
        "That's the screener: pick tickers and a direction, choose your expiration and strike trade-offs, then stress-test the contracts in the heatmap and drill into the Detail View.",
        'Relaunch this tour anytime from the Guide button next to the page title.',
      ],
    },
  ];

  return { formSteps, resultsSteps };
}
